import { useState, useEffect, useRef } from 'react';
import { formatDate, stripHtml } from '../utils';
import useLazyImage from '../hooks/useLazyImage';
import ExportMenu from './ExportMenu';

// ─── TREE TIMELINE COMPONENT ───────────────────────────────────────────────────
//
//  Renders events as a branching tree structure. Events with no parentEventId
//  appear on the main trunk; events with parentEventId branch off their parent.
//
//  Props:
//    events       {array}    event objects (may include parentEventId, branchLabel,
//                            branchColor, collapsed fields)
//    colors       {object}   { primary, secondary, tertiary }
//    onEventClick {function} called with the event object
//    manualOrder  {boolean}  unused — tree order is by date/parent
//

// ── Constants ──────────────────────────────────────────────────────────────────

const LEVEL_GAP  = 220;   // px between generation levels
const NODE_SEP   = 78;    // min spacing between sibling nodes
const PADDING    = 90;    // canvas padding
const NR         = { root: 20, branch: 15, leaf: 11 }; // node radii
const MS_H       = 13;    // milestone diamond half-size
const TRUNK_W    = 5;
const PREVIEW_W  = 270;

const PALETTE = [
  '#4A8FD4','#D96B3B','#5AA35C','#9059A8','#D4A83B',
  '#1A9E8C','#C94444','#5B8FD4','#3FAF70','#C04A9A',
];

// ── Tree building ──────────────────────────────────────────────────────────────

const buildTree = (events) => {
  const nm = {};
  events.forEach(ev => { nm[ev.id] = { ev, children: [], depth: 0 }; });

  const roots = [];
  events.forEach(ev => {
    const pid = ev.parentEventId;
    if (pid && nm[pid]) nm[pid].children.push(ev.id);
    else roots.push(ev.id);
  });

  const byDate = (a, b) => ((nm[a].ev.startDate || "").localeCompare(nm[b].ev.startDate || ""));
  roots.sort(byDate);
  Object.values(nm).forEach(n => n.children.sort(byDate));

  const setDepth = (id, d) => { nm[id].depth = d; nm[id].children.forEach(c => setDepth(c, d + 1)); };
  roots.forEach(id => setDepth(id, 0));

  return { nm, roots };
};

// ── Branch color assignment ────────────────────────────────────────────────────

const buildColors = (nm, roots) => {
  const out = {};
  let pi = 0;
  const walk = (id, inh) => {
    const color = nm[id].ev.branchColor || inh;
    out[id] = color;
    nm[id].children.forEach(c => walk(c, color));
  };
  roots.forEach(id => walk(id, PALETTE[pi++ % PALETTE.length]));
  return out;
};

// ── Layout (simplified Reingold-Tilford) ──────────────────────────────────────

const computeLayout = (nm, roots, orient, collapsed) => {
  let leaf = 0;

  const assignPos = (id) => {
    const vis = collapsed.has(id) ? [] : nm[id].children;
    if (!vis.length) { nm[id]._p = leaf++; return; }
    vis.forEach(c => assignPos(c));
    const f = nm[vis[0]]._p, l = nm[vis[vis.length - 1]]._p;
    nm[id]._p = (f + l) / 2;
  };
  roots.forEach(id => assignPos(id));

  const pos = {};
  Object.keys(nm).forEach(id => {
    const n = nm[id];
    pos[id] = orient === 'tb'
      ? { x: n._p * NODE_SEP + PADDING, y: n.depth * LEVEL_GAP + PADDING }
      : { x: n.depth * LEVEL_GAP + PADDING, y: n._p * NODE_SEP + PADDING };
  });
  return pos;
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const getSubtree = (id, nm) => {
  const s = new Set([id]);
  const w = (n) => nm[n].children.forEach(c => { s.add(c); w(c); });
  w(id);
  return s;
};

const getAncestors = (id, nm) => {
  const s = new Set();
  let c = nm[id]?.ev.parentEventId;
  while (c && nm[c]) { s.add(c); c = nm[c].ev.parentEventId; }
  return s;
};

const countDesc = (id, nm) =>
  nm[id].children.reduce((s, c) => s + 1 + countDesc(c, nm), 0);

const lw = (depth) => Math.max(1, TRUNK_W - depth * 0.65);

// ─────────────────────────────────────────────────────────────────────────────

// Sub-component so useLazyImage can be called legally outside a .map() loop.
const TreeNodeImage = ({ src, x, y, size, clipId }) => {
  const imgRef = useLazyImage(src);
  return (
    <image
      ref={imgRef}
      x={x} y={y}
      width={size} height={size}
      clipPath={`url(#${clipId})`}
      preserveAspectRatio="xMidYMid slice"
      style={{ pointerEvents: 'none' }}
    />
  );
};

const TreeTimeline = ({ events, colors, onEventClick, timeline = null, isEmbed = false }) => {
  const [orient,      setOrient]      = useState('lr');
  const [collapsed,   setCollapsed]   = useState(new Set());
  const [selected,    setSelected]    = useState(null);
  const [hovered,     setHovered]     = useState(null);
  const [hoverScrPos, setHoverScrPos] = useState({ x: 0, y: 0 });
  const [isolated,    setIsolated]    = useState(null);
  const [search,      setSearch]      = useState('');
  const [matchIdx,    setMatchIdx]    = useState(0);
  const [zoom,        setZoom]        = useState(1);
  const [pan,         setPan]         = useState({ x: 0, y: 0 });

  const containerRef = useRef(null);
  const isPanning    = useRef(false);
  const panStart     = useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const clickTimer   = useRef(null);
  const posRef       = useRef({});
  const zoomRef      = useRef(1);
  const panRef       = useRef(pan);
  const visIdsRef    = useRef(new Set());
  const nmRef        = useRef({});

  // ── Tree data ────────────────────────────────────────────────────────────────
  const { nm, roots }    = buildTree(events);
  const branchColors     = buildColors(nm, roots);
  const positions        = computeLayout(nm, roots, orient, collapsed);
  posRef.current         = positions;
  zoomRef.current        = zoom;
  panRef.current         = pan;
  nmRef.current          = nm;

  // Visible nodes
  const visIds = new Set();
  const addVis = (id) => {
    if (isolated && !getSubtree(isolated, nm).has(id)) return;
    visIds.add(id);
    if (!collapsed.has(id)) nm[id].children.forEach(c => addVis(c));
  };
  if (isolated && nm[isolated]) addVis(isolated);
  else roots.forEach(id => addVis(id));
  visIdsRef.current      = visIds;

  // Canvas bounds
  let maxX = 500, maxY = 400;
  visIds.forEach(id => {
    const p = positions[id];
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  });
  const canvasW = maxX + 200;
  const canvasH = maxY + 120;

  // Search matches
  const sq = search.toLowerCase().trim();
  const matches = sq
    ? [...visIds].filter(id => nm[id].ev.title.toLowerCase().includes(sq))
    : [];
  const curMatch = matches[matchIdx] || null;

  // Highlight path on hover
  const highlightEdges = new Set();
  if (hovered) {
    let c = hovered;
    while (c && nm[c]?.ev.parentEventId) {
      const p = nm[c].ev.parentEventId;
      if (p) highlightEdges.add(`${p}|${c}`);
      c = p;
    }
  }

  // Roots on screen for trunk
  const visRoots = roots.filter(id => visIds.has(id));

  // ── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const onMove = (e) => {
      if (!isPanning.current) return;
      setPan({ x: panStart.current.px + e.clientX - panStart.current.mx,
               y: panStart.current.py + e.clientY - panStart.current.my });
    };
    const onUp = () => { isPanning.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoom(z => Math.min(3, Math.max(0.15, z * (e.deltaY < 0 ? 1.12 : 0.89))));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Auto-pan to search match
  useEffect(() => {
    if (!matches.length) return;
    const p = posRef.current[matches[matchIdx]];
    if (!p || !containerRef.current) return;
    const { width, height } = containerRef.current.getBoundingClientRect();
    setPan({ x: width / 2 - p.x * zoomRef.current, y: height / 2 - p.y * zoomRef.current });
  }, [matchIdx, matches.join(',')]); // eslint-disable-line

  // ── Touch: two-finger pan + pinch zoom + double-tap focus + long-press collapse
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.style.touchAction = 'none';

    let t1Start         = null;  // { x, y, t } single-finger start
    let twoStart        = null;  // { midX, midY, panX, panY, pinchDist }
    let lastTapTime     = 0;
    let lastTapX        = 0;
    let lastTapY        = 0;
    let longPressTimer  = null;
    let moved           = false;
    let prevPinchScale  = 1;

    const dist2  = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const midPt  = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });
    const clearLP = () => { if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; } };

    const screenToSvg = (sx, sy) => {
      const rect = el.getBoundingClientRect();
      return {
        x: ((sx - rect.left) - panRef.current.x) / zoomRef.current,
        y: ((sy - rect.top)  - panRef.current.y) / zoomRef.current,
      };
    };

    const nearestNode = (svgX, svgY, filter, maxDist = 80) => {
      let best = null, bestD = Infinity;
      for (const id of visIdsRef.current) {
        if (filter && !filter(id)) continue;
        const p = posRef.current[id];
        if (!p) continue;
        const d = Math.hypot(svgX - p.x, svgY - p.y);
        if (d < bestD && d < maxDist) { bestD = d; best = id; }
      }
      return best;
    };

    const onStart = (e) => {
      moved = false;
      clearLP();

      if (e.touches.length >= 2) {
        t1Start = null;
        const m = midPt(e.touches[0], e.touches[1]);
        const d = dist2(e.touches[0], e.touches[1]);
        twoStart = { midX: m.x, midY: m.y, panX: panRef.current.x, panY: panRef.current.y, pinchDist: d };
        prevPinchScale = 1;
        return;
      }

      twoStart = null;
      if (e.touches.length === 1) {
        const t = e.touches[0];
        t1Start = { x: t.clientX, y: t.clientY, t: Date.now() };
        const tx = t.clientX, ty = t.clientY;
        longPressTimer = setTimeout(() => {
          if (moved) return;
          longPressTimer = null;
          const { x: svgX, y: svgY } = screenToSvg(tx, ty);
          const id = nearestNode(svgX, svgY, (id) => nmRef.current[id]?.children.length > 0, 60);
          if (id) {
            setCollapsed(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
          }
        }, 500);
      }
    };

    const onMove = (e) => {
      if (e.touches.length >= 2 && twoStart) {
        e.preventDefault();
        const m = midPt(e.touches[0], e.touches[1]);
        setPan({ x: twoStart.panX + m.x - twoStart.midX, y: twoStart.panY + m.y - twoStart.midY });
        const d     = dist2(e.touches[0], e.touches[1]);
        const scale = d / twoStart.pinchDist;
        const dScale = scale / prevPinchScale;
        if (dScale > 0.5 && dScale < 2) {
          setZoom(z => Math.max(0.15, Math.min(3, z * dScale)));
        }
        prevPinchScale = scale;
        return;
      }

      if (t1Start && e.touches.length === 1) {
        const dx = Math.abs(e.touches[0].clientX - t1Start.x);
        const dy = Math.abs(e.touches[0].clientY - t1Start.y);
        if (dx > 8 || dy > 8) { moved = true; clearLP(); }
      }
    };

    const onEnd = (e) => {
      clearLP();
      twoStart = null;
      if (!t1Start || moved || e.changedTouches.length === 0) { t1Start = null; return; }

      const t = e.changedTouches[0];
      const now = Date.now();
      if (now - lastTapTime < 300 && Math.hypot(t.clientX - lastTapX, t.clientY - lastTapY) < 40) {
        // Double tap → zoom into nearest node
        const { x: svgX, y: svgY } = screenToSvg(t.clientX, t.clientY);
        const id = nearestNode(svgX, svgY, null, 120);
        if (id) {
          const p = posRef.current[id];
          const rect = el.getBoundingClientRect();
          const targetZoom = Math.min(3, Math.max(1, zoomRef.current * 1.5));
          setPan({ x: rect.width / 2 - p.x * targetZoom, y: rect.height / 2 - p.y * targetZoom });
          setZoom(targetZoom);
        }
        lastTapTime = 0;
      } else {
        lastTapTime = now;
        lastTapX = t.clientX;
        lastTapY = t.clientY;
      }
      t1Start = null;
    };

    const onCancel = () => { clearLP(); t1Start = null; twoStart = null; };

    el.addEventListener('touchstart',  onStart,  { passive: true });
    el.addEventListener('touchmove',   onMove,   { passive: false });
    el.addEventListener('touchend',    onEnd,    { passive: true });
    el.addEventListener('touchcancel', onCancel, { passive: true });

    return () => {
      el.style.touchAction = '';
      el.removeEventListener('touchstart',  onStart);
      el.removeEventListener('touchmove',   onMove);
      el.removeEventListener('touchend',    onEnd);
      el.removeEventListener('touchcancel', onCancel);
      clearLP();
    };
  }, []); // all dynamic values accessed via refs

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleNodeClick = (id, e) => {
    e.stopPropagation();
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      onEventClick(nm[id].ev);
      return;
    }
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      setSelected(s => s === id ? null : id);
    }, 220);
  };

  const toggleCollapse = (id, e) => {
    e.stopPropagation();
    setCollapsed(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const fitToScreen = () => {
    const el = containerRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setZoom(Math.min(1, width / canvasW, height / canvasH) * 0.88);
    setPan({ x: 0, y: 0 });
  };

  const handleKeyDown = (e) => {
    if (!selected || !nm[selected]) return;
    const node = nm[selected];
    const pid = node.ev.parentEventId;
    const siblings = pid && nm[pid] ? nm[pid].children : roots;
    const si = siblings.indexOf(selected);
    const visChildren = collapsed.has(selected) ? [] : node.children;

    const go = (id) => { if (id) { e.preventDefault(); setSelected(id); } };

    if (orient === 'lr') {
      if (e.key === 'ArrowRight') go(visChildren[0]);
      else if (e.key === 'ArrowLeft') go(pid);
      else if (e.key === 'ArrowUp' && si > 0) go(siblings[si - 1]);
      else if (e.key === 'ArrowDown' && si < siblings.length - 1) go(siblings[si + 1]);
    } else {
      if (e.key === 'ArrowDown') go(visChildren[0]);
      else if (e.key === 'ArrowUp') go(pid);
      else if (e.key === 'ArrowLeft' && si > 0) go(siblings[si - 1]);
      else if (e.key === 'ArrowRight' && si < siblings.length - 1) go(siblings[si + 1]);
    }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEventClick(node.ev); }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  if (!events.length) return (
    <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>
      No events yet. Add your first event.
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="tree-root"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >

      {/* ── Controls bar ── */}
      <div className="tree-ctrl-bar">
        <div className="tree-ctrl-group">
          {[['lr', 'Left → Right'], ['tb', 'Top ↓ Bottom']].map(([o, lbl]) => (
            <button
              key={o}
              className={`tree-ctrl-btn${orient === o ? ' tree-ctrl-active' : ''}`}
              onClick={() => setOrient(o)}
              style={orient === o ? { borderColor: colors.primary, color: colors.primary, background: `${colors.primary}15` } : {}}
            >
              {lbl}
            </button>
          ))}
        </div>

        <div className="tree-ctrl-group">
          <button className="tree-ctrl-btn" onClick={() => {
            const ids = Object.keys(nm).filter(id => nm[id].children.length > 0);
            setCollapsed(new Set(ids));
          }}>Collapse All</button>
          <button className="tree-ctrl-btn" onClick={() => setCollapsed(new Set())}>Expand All</button>
          {isolated && (
            <button
              className="tree-ctrl-btn"
              style={{ color: colors.primary, borderColor: colors.primary }}
              onClick={() => setIsolated(null)}
            >
              ✕ Show All
            </button>
          )}
        </div>

        <div className="tree-ctrl-group">
          <button className="tree-ctrl-btn" onClick={() => setZoom(z => Math.min(3, z * 1.2))} title="Zoom in">＋</button>
          <button className="tree-ctrl-btn" onClick={() => setZoom(z => Math.max(0.15, z * 0.82))} title="Zoom out">－</button>
          <button className="tree-ctrl-btn" onClick={fitToScreen} title="Fit to screen">⊡</button>
        </div>

        <div className="tree-ctrl-search">
          <input
            className="tree-search-input"
            value={search}
            onChange={e => { setSearch(e.target.value); setMatchIdx(0); }}
            placeholder="Search nodes…"
          />
          {matches.length > 0 && (
            <>
              <span className="tree-search-count">{matchIdx + 1}/{matches.length}</span>
              <button className="tree-ctrl-btn tree-ctrl-sm" onClick={() => setMatchIdx(i => (i - 1 + matches.length) % matches.length)}>‹</button>
              <button className="tree-ctrl-btn tree-ctrl-sm" onClick={() => setMatchIdx(i => (i + 1) % matches.length)}>›</button>
            </>
          )}
        </div>

        {!isEmbed && timeline && (
          <ExportMenu timeline={timeline} captureRef={containerRef} />
        )}
      </div>

      {/* ── Pan/zoom viewport ── */}
      <div
        className="tree-viewport"
        onMouseDown={e => {
          if (e.button !== 0) return;
          isPanning.current = true;
          panStart.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
        }}
        onClick={() => setSelected(null)}
      >
        <div
          className="tree-canvas-wrap"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0', width: canvasW, height: canvasH }}
        >
          <svg width={canvasW} height={canvasH} className="tree-svg">

            {/* Trunk — connects all visible root nodes */}
            {visRoots.length > 1 && (() => {
              const first = positions[visRoots[0]];
              const last  = positions[visRoots[visRoots.length - 1]];
              return (
                <line
                  x1={first.x} y1={first.y}
                  x2={last.x}  y2={last.y}
                  stroke={colors.primary}
                  strokeWidth={TRUNK_W}
                  strokeLinecap="round"
                  opacity={0.7}
                />
              );
            })()}

            {/* Branch edges */}
            {[...visIds].map(id => {
              const visChildren = collapsed.has(id) ? [] : nm[id].children.filter(c => visIds.has(c));
              return visChildren.map(cId => {
                const p  = positions[id];
                const c  = positions[cId];
                const col = branchColors[cId] || colors.secondary;
                const lineW = lw(nm[id].depth);
                const edgeKey = `${id}|${cId}`;
                const isHot = highlightEdges.has(edgeKey);
                let d;
                if (orient === 'lr') {
                  const dx = (c.x - p.x) * 0.44;
                  d = `M ${p.x} ${p.y} C ${p.x + dx} ${p.y} ${c.x - dx} ${c.y} ${c.x} ${c.y}`;
                } else {
                  const dy = (c.y - p.y) * 0.44;
                  d = `M ${p.x} ${p.y} C ${p.x} ${p.y + dy} ${c.x} ${c.y - dy} ${c.x} ${c.y}`;
                }
                return (
                  <path
                    key={edgeKey}
                    d={d}
                    stroke={col}
                    strokeWidth={isHot ? lineW + 2 : lineW}
                    fill="none"
                    strokeLinecap="round"
                    opacity={isHot ? 1 : 0.7}
                    className="tree-edge"
                  />
                );
              });
            })}

            {/* Nodes */}
            {[...visIds].map(id => {
              const pos  = positions[id];
              const node = nm[id];
              const ev   = node.ev;
              const col  = branchColors[id] || colors.secondary;
              const isRoot    = !ev.parentEventId;
              const isLeaf    = node.children.length === 0;
              const isMilest  = ev.startDate === ev.endDate;
              const isCollaps = collapsed.has(id) && node.children.length > 0;
              const isSel     = selected === id;
              const isHov     = hovered === id;
              const isMatch   = matches.includes(id);
              const isCurMatch = curMatch === id;
              const hidden    = isCollaps ? countDesc(id, nm) : 0;
              const r         = isRoot ? NR.root : isLeaf ? NR.leaf : NR.branch;

              // Label alternation: even _pos → above/left, odd → below/right
              const side  = Math.round(node._p) % 2 === 0 ? -1 : 1;
              const label = ev.title.length > 20 ? ev.title.slice(0, 19) + '…' : ev.title;
              const dateStr = formatDate(ev.startDate, ev.startEra !== 'NONE');

              // Toggle button position (right of node for LR, below for TB)
              const tbx = orient === 'lr' ? pos.x + r + 10 : pos.x;
              const tby = orient === 'lr' ? pos.y          : pos.y + r + 10;

              // Label position
              const lx = orient === 'lr' ? pos.x : pos.x + side * (r + 16);
              const ly = orient === 'lr' ? pos.y + side * (r + 18) : pos.y;
              const anchor = orient === 'lr' ? 'middle' : side > 0 ? 'start' : 'end';
              const labelW = Math.min(160, label.length * 6.5 + 16);

              return (
                <g key={id}>
                  {/* Selection pulse ring */}
                  {isSel && (
                    <circle cx={pos.x} cy={pos.y} r={r + 8} fill="none"
                      stroke={colors.primary} strokeWidth={2} className="tree-sel-ring" />
                  )}

                  {/* Search highlight */}
                  {isMatch && (
                    <circle cx={pos.x} cy={pos.y} r={r + 5}
                      fill={isCurMatch ? `${colors.primary}30` : `${colors.secondary}22`}
                      stroke={isCurMatch ? colors.primary : colors.secondary}
                      strokeWidth={1.5} />
                  )}

                  {/* Node shape */}
                  {isMilest ? (
                    <rect
                      x={pos.x - MS_H} y={pos.y - MS_H}
                      width={MS_H * 2} height={MS_H * 2}
                      rx={2} fill={col} stroke="var(--bg)" strokeWidth={2}
                      transform={`rotate(45,${pos.x},${pos.y})`}
                      style={{ cursor: 'pointer' }}
                      onClick={e => handleNodeClick(id, e)}
                      onMouseEnter={e => { setHovered(id); setHoverScrPos({ x: e.clientX, y: e.clientY }); }}
                      onMouseLeave={() => setHovered(null)}
                    />
                  ) : (
                    <>
                      <circle
                        cx={pos.x} cy={pos.y} r={r}
                        fill={ev.photo ? 'transparent' : col}
                        stroke={ev.photo ? col : (isRoot ? 'var(--bg)' : 'none')}
                        strokeWidth={isRoot ? 3 : 2}
                        style={{ cursor: 'pointer', filter: isHov ? 'brightness(1.18)' : 'none', transition: 'filter 0.12s' }}
                        onClick={e => handleNodeClick(id, e)}
                        onMouseEnter={e => { setHovered(id); setHoverScrPos({ x: e.clientX, y: e.clientY }); }}
                        onMouseLeave={() => setHovered(null)}
                      />
                      {ev.photo && (
                        <>
                          <defs>
                            <clipPath id={`tc-${id}`}>
                              <circle cx={pos.x} cy={pos.y} r={r - 2} />
                            </clipPath>
                          </defs>
                          <TreeNodeImage
                            src={ev.photo}
                            x={pos.x - r + 2} y={pos.y - r + 2}
                            size={(r - 2) * 2}
                            clipId={`tc-${id}`}
                          />
                          <circle cx={pos.x} cy={pos.y} r={r} fill="transparent" stroke={col} strokeWidth={2.5}
                            style={{ cursor: 'pointer' }}
                            onClick={e => handleNodeClick(id, e)}
                            onMouseEnter={e => { setHovered(id); setHoverScrPos({ x: e.clientX, y: e.clientY }); }}
                            onMouseLeave={() => setHovered(null)}
                          />
                        </>
                      )}
                    </>
                  )}

                  {/* Collapse/expand toggle */}
                  {node.children.length > 0 && (
                    <g onClick={e => toggleCollapse(id, e)} style={{ cursor: 'pointer' }} role="button" aria-label={isCollaps ? 'Expand' : 'Collapse'}>
                      <circle cx={tbx} cy={tby} r={9}
                        fill={isCollaps ? col : 'var(--surface)'}
                        stroke={col} strokeWidth={1.5} />
                      <text x={tbx} y={tby + 4} textAnchor="middle"
                        fill={isCollaps ? '#fff' : col}
                        fontSize={isCollaps && hidden > 9 ? 8 : 11}
                        fontWeight={700} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                        {isCollaps ? (hidden > 99 ? '…' : `+${hidden}`) : '−'}
                      </text>
                    </g>
                  )}

                  {/* Label card */}
                  <g style={{ pointerEvents: 'none' }}>
                    {orient === 'lr' ? (
                      <>
                        <rect
                          x={lx - labelW / 2} y={ly + (side > 0 ? 2 : -28)}
                          width={labelW} height={26}
                          rx={4} fill="var(--surface)" stroke="var(--border)" strokeWidth={0.5}
                        />
                        <text x={lx} y={ly + (side > 0 ? 13 : -14)} textAnchor="middle"
                          fill="var(--text)" fontSize={10} fontWeight={600}>{label}</text>
                        <text x={lx} y={ly + (side > 0 ? 23 : -5)} textAnchor="middle"
                          fill="var(--text3)" fontSize={8}>{dateStr}</text>
                      </>
                    ) : (
                      <>
                        <rect
                          x={lx + (side > 0 ? 4 : -labelW - 4)} y={ly - 13}
                          width={labelW} height={26}
                          rx={4} fill="var(--surface)" stroke="var(--border)" strokeWidth={0.5}
                        />
                        <text x={lx + (side > 0 ? labelW / 2 + 4 : -labelW / 2 - 4)} y={ly - 2}
                          textAnchor="middle" fill="var(--text)" fontSize={10} fontWeight={600}>{label}</text>
                        <text x={lx + (side > 0 ? labelW / 2 + 4 : -labelW / 2 - 4)} y={ly + 9}
                          textAnchor="middle" fill="var(--text3)" fontSize={8}>{dateStr}</text>
                      </>
                    )}

                    {/* Branch label at fork point */}
                    {ev.branchLabel && ev.parentEventId && nm[ev.parentEventId] && (() => {
                      const pp = positions[ev.parentEventId];
                      const mx = (pp.x + pos.x) / 2;
                      const my = (pp.y + pos.y) / 2;
                      return (
                        <text x={mx} y={my - 6} textAnchor="middle"
                          fill={col} fontSize={9} fontStyle="italic" fontWeight={600}>
                          {ev.branchLabel}
                        </text>
                      );
                    })()}
                  </g>
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* ── Hover preview card ── */}
      {hovered && nm[hovered] && (() => {
        const ev = nm[hovered].ev;
        const col = branchColors[hovered] || colors.secondary;
        const desc = stripHtml(ev.description || '').slice(0, 110);
        const hasChildren = nm[hovered].children.length > 0;
        return (
          <div
            className="tree-preview"
            style={{
              left: Math.min(hoverScrPos.x + 14, window.innerWidth - PREVIEW_W - 12),
              top:  hoverScrPos.y + 12,
            }}
          >
            <div className="tree-preview-pip" style={{ background: col }} />
            <div className="tree-preview-title">{ev.title}</div>
            <div className="tree-preview-date">
              {formatDate(ev.startDate, ev.startEra !== 'NONE')}
              {ev.startDate !== ev.endDate && <> — {formatDate(ev.endDate, ev.endEra !== 'NONE')}</>}
            </div>
            {desc && <div className="tree-preview-desc">{desc}{(ev.description?.length || 0) > 110 ? '…' : ''}</div>}
            <div className="tree-preview-actions">
              <button
                className="tree-preview-btn"
                style={{ borderColor: colors.primary, color: colors.primary }}
                onClick={e => { e.stopPropagation(); onEventClick(ev); }}
              >
                View Details
              </button>
              {hasChildren && !isolated && (
                <button
                  className="tree-preview-btn"
                  style={{ borderColor: col, color: col }}
                  onClick={e => { e.stopPropagation(); setIsolated(hovered); setHovered(null); }}
                >
                  Isolate Branch
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Hint */}
      <div className="tree-hint">
        Ctrl+scroll to zoom · drag to pan · click node to preview · double-click to open
        · pinch to zoom · two-finger drag to pan · long-press branch to collapse
      </div>
    </div>
  );
};

export default TreeTimeline;
